import { useState } from "react";
import { useAbsences, type AbsenceStatus } from "@/hooks/use-absences";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { Button } from "@/components/ui/button";
import { formatDate, formatHours } from "@/lib/format";
import { ReportAbsenceDialog } from "@/components/shared/ReportAbsenceDialog";
import { formatAbsenceTimeRange } from "@/lib/absence-time";
import { ABSENCE_TYPE_LABELS } from "@/lib/absence-display";
import { summarizeAbsences } from "@/lib/employee-card";
import { TONE_PILL, type StatusTone } from "@/lib/widget-status";
import { cn } from "@/lib/utils";
import { TabLoading } from "./TabLoading";

const STATUS: Record<AbsenceStatus, { label: string; tone: StatusTone }> = {
  approved: { label: "Godkjent", tone: "reached" },
  pending: { label: "Venter", tone: "near" },
  rejected: { label: "Avslått", tone: "miss" },
};

/**
 * VIB-1221: `canManage` (leder/admin) viser
 * «Registrer fravær»-knappen, som åpner ReportAbsenceDialog for DENNE
 * ansatte (userId). Audit-aktøren er alltid den faktisk innloggede lederen
 * (useAuth() inne i useCreateAbsence) — userId her er kun target/on_behalf_of.
 */
export function FravaerTab({ userId, canManage = false }: { userId: string; canManage?: boolean }) {
  const { data: absences, isLoading } = useAbsences(userId);
  const year = new Date().getFullYear();
  const [showDialog, setShowDialog] = useState(false);

  if (isLoading) return <TabLoading />;

  const { rows, approvedHours, pendingHours } = summarizeAbsences(absences ?? [], year);

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex flex-wrap items-center justify-between gap-2 px-5 py-3">
          <div className="flex flex-wrap items-baseline gap-x-2.5">
            <h2 className="font-display text-base font-semibold">Fravær {year}</h2>
            <span className="text-sm tabular-nums">
              <b>{formatHours(approvedHours)}</b> godkjent
              {pendingHours > 0 && <> · {formatHours(pendingHours)} venter</>}
            </span>
          </div>
          {canManage && (
            <Button size="sm" variant="outline" onClick={() => setShowDialog(true)} className="gap-1.5">
              <SrIcon name="plus" className="h-3.5 w-3.5" />
              Registrer fravær
            </Button>
          )}
        </div>

        {rows.length === 0 ? (
          <p className="border-t border-border px-5 py-6 text-sm text-muted-foreground">Ingen registrert fravær.</p>
        ) : (
          <div className="overflow-x-auto border-t border-border">
            <table className="w-full text-sm" aria-label="Fravær">
              <thead>
                <tr className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-5 py-2 text-left font-medium">Type</th>
                  <th className="px-3 py-2 text-left font-medium">Periode</th>
                  <th className="px-3 py-2 text-left font-medium">Varighet</th>
                  <th className="px-3 py-2 text-right font-medium">Timer</th>
                  <th className="px-5 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(({ absence: a, workingDays, hours }) => {
                  const rejected = a.status === "rejected";
                  const timeRange = formatAbsenceTimeRange(a.start_time, a.end_time);
                  const status = STATUS[a.status] ?? { label: a.status, tone: "near" as const };
                  return (
                    <tr key={a.id} className="border-b border-border/50 last:border-0">
                      <td className={cn("px-5 py-2.5 font-medium", rejected && "text-muted-foreground line-through")}>
                        {ABSENCE_TYPE_LABELS[a.type] ?? a.type}
                      </td>
                      <td className={cn("px-3 py-2.5 tabular-nums text-muted-foreground", rejected && "line-through")}>
                        {formatDate(a.start_date)}
                        {timeRange ? <> kl. {timeRange}</> : a.start_date !== a.end_date ? <> → {formatDate(a.end_date)}</> : null}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        {workingDays == null
                          ? "Deldag"
                          : `${workingDays} ${workingDays === 1 ? "arbeidsdag" : "arbeidsdager"}`}
                      </td>
                      <td className={cn("px-3 py-2.5 text-right tabular-nums", rejected ? "text-muted-foreground line-through" : "font-medium")}>
                        {formatHours(hours)}
                      </td>
                      <td className="px-5 py-2.5">
                        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", TONE_PILL[status.tone])}>
                          {status.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      {canManage && <ReportAbsenceDialog open={showDialog} onOpenChange={setShowDialog} userId={userId} />}
    </Card>
  );
}
